import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { monthRange, monthLabel } from "@/pages/SummaryPage";
import { Statement } from "@/components/Statement";
import { ShareButtons } from "@/components/ShareButtons";
import { useAuth } from "@/context/AuthContext";

const range = (m) => Object.fromEntries(new URLSearchParams(monthRange(m)));

export default function PrintPage() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const month = search.get("month") || "";
  const [daily, setDaily] = useState(null);
  const [sett, setSett] = useState(null);
  const { can } = useAuth();

  useEffect(() => {
    if (!/^\d{4}-\d{2}$/.test(month)) return;
    const params = range(month);
    const settReq = can("sett") ? api.get(`/books/${id}/sheet`, { params: { ...params, sheet: "sett" } }) : Promise.resolve({ data: null });
    Promise.all([api.get(`/books/${id}/sheet`, { params }), settReq])
      .then(([a, b]) => { setDaily(a.data); setSett(b.data); })
      .catch((e) => toast.error(errMsg(e)));
  }, [id, month]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!can("export")) return <p className="p-10 text-sm" data-testid="print-no-permission">You don't have permission to print or share statements.</p>;
  if (!/^\d{4}-\d{2}$/.test(month)) return <p className="p-10 text-sm" data-testid="print-invalid">Pick a month from the Monthly summary.</p>;
  if (!daily) return <p className="p-10 text-sm text-slate-500" data-testid="print-loading">Preparing page…</p>;

  return (
    <div className="min-h-screen bg-slate-200 print:bg-white">
      <div className="no-print sticky top-0 z-10 bg-slate-900 text-white">
        <div className="max-w-[1100px] mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/summary" data-testid="print-back-link" className="flex items-center gap-1.5 text-sm font-semibold hover:underline"><ArrowLeft size={15} /> <span className="hidden sm:inline">Monthly summary</span><span className="sm:hidden">Back</span></Link>
          <span className="ml-auto" />
          <ShareButtons text={`${daily.book.name} — ${monthLabel(month)} statement`}
            makeUrl={async () => `${window.location.origin}/s/${(await api.post(`/books/${id}/share`, { month })).data.token}`} />
          <button data-testid="print-btn" onClick={() => window.print()}
            className="h-9 px-4 bg-yellow-300 text-slate-900 font-bold flex items-center gap-2 hover:bg-yellow-200 transition-colors">
            <Printer size={15} /> <span className="hidden sm:inline">Print / Save PDF</span>
          </button>
        </div>
      </div>
      <div className="max-w-[1100px] mx-auto my-6 print:my-0 bg-white p-6 sm:p-10 print:p-0 shadow-sm print:shadow-none overflow-x-auto" data-testid="print-page">
        <Statement name={daily.book.name} month={month} daily={daily} sett={sett} />
      </div>
    </div>
  );
}
